import { Controller } from "@hotwired/stimulus"

// Searchable assignees picker. Renders selected users as chips, exposes a
// search input that fetches /taches_pickers/users?q=, and adds/removes
// assignments_attributes hidden fields so Rails strong params accepts the
// nested attributes.
//
// Server-side accepts_nested_attributes_for(:assignments) requires :user_id,
// :role, optional :id (for existing) and :_destroy. We always render fully-
// formed sets : selected users have user_id+role(+id), and removed ones get
// _destroy=1 with their original id.
export default class extends Controller {
  static targets = ["chips", "input", "results", "hidden", "empty"]
  static values  = { searchUrl: String }

  connect() {
    this._timer = null
    this._byId  = new Map()        // user_id -> { full_name, avatar, existingId, destroyed }
    this._readInitialState()
    this._renderChips()
    document.addEventListener("click", this._onDocClick = (e) => {
      if (!this.element.contains(e.target)) this._closeResults()
    })
  }

  disconnect() {
    document.removeEventListener("click", this._onDocClick)
  }

  _readInitialState() {
    // Pre-existing assignees rendered server-side as <span data-initial-user>.
    // We hydrate the in-memory map from those nodes, then remove them from the
    // DOM (they were just data carriers, not visible UI). The chips are then
    // rendered fresh from the map by _renderChips() in connect().
    this.element.querySelectorAll("[data-initial-user]").forEach((el) => {
      try {
        const u = JSON.parse(el.dataset.initialUser)
        this._byId.set(u.id, {
          full_name:  u.full_name,
          avatar:     u.avatar,
          existingId: u.existingId,
          destroyed:  false
        })
      } catch (_e) { /* ignore malformed payloads */ }
      el.remove()
    })
  }

  search(event) {
    const q = event.target.value
    clearTimeout(this._timer)
    this._timer = setTimeout(() => this._fetch(q), 200)
  }

  async _fetch(q) {
    if (!this.searchUrlValue) return
    const url = new URL(this.searchUrlValue, window.location.origin)
    url.searchParams.set("q", q || "")
    const res = await fetch(url.toString(), { headers: { Accept: "application/json" } })
    if (!res.ok) return
    const data = await res.json()
    this._renderResults(data)
  }

  _renderResults(items) {
    if (!this.hasResultsTarget) return
    this.resultsTarget.innerHTML = ""
    if (!items.length) {
      this.resultsTarget.innerHTML = `<div class="picker__empty">Aucun résultat</div>`
      this._positionResults()
      this.resultsTarget.classList.add("picker__results--open")
      return
    }
    items.forEach((u) => {
      if (this._byId.has(u.id) && !this._byId.get(u.id).destroyed) return
      const row = document.createElement("button")
      row.type = "button"
      row.className = "picker__result"
      row.innerHTML = `
        ${u.avatar
          ? `<span class="picker__avatar"><img src="${u.avatar}" alt=""></span>`
          : `<span class="picker__avatar picker__avatar--initials">${this._initials(u.full_name || u.email)}</span>`}
        <span class="picker__result-label">${this._escape(u.full_name || u.email)}</span>
      `
      row.addEventListener("click", (event) => {
        event.preventDefault()
        event.stopPropagation()
        this._select(u)
      })
      this.resultsTarget.appendChild(row)
    })
    this._positionResults()
    this.resultsTarget.classList.add("picker__results--open")
  }

  _positionResults() {
    if (!this.hasInputTarget || !this.hasResultsTarget) return
    const rect = this.inputTarget.getBoundingClientRect()
    this.resultsTarget.style.top   = `${rect.bottom + 4}px`
    this.resultsTarget.style.left  = `${rect.left}px`
    this.resultsTarget.style.width = `${rect.width}px`
  }

  _select(user) {
    const existing = this._byId.get(user.id)
    if (existing) {
      existing.destroyed = false
    } else {
      this._byId.set(user.id, {
        full_name:  user.full_name || user.email,
        avatar:     user.avatar,
        existingId: null,
        destroyed:  false
      })
    }
    if (this.hasInputTarget) this.inputTarget.value = ""
    this._closeResults()
    this._renderChips()
    this._renderHidden()
  }

  removeChip(event) {
    // Stop propagation : sinon le click bubble jusqu'au modal-close#backdrop
    // qui détecte l'élément retiré du DOM comme "click hors panel" et ferme la
    // modale. Idem pour preventDefault, le bouton est dans un form qui ferait
    // un submit par défaut.
    event.preventDefault()
    event.stopPropagation()
    const id = parseInt(event.currentTarget.dataset.userId, 10)
    const cur = this._byId.get(id)
    if (!cur) return
    if (cur.existingId) cur.destroyed = true
    else this._byId.delete(id)
    this._renderChips()
    this._renderHidden()
  }

  _renderChips() {
    if (!this.hasChipsTarget) return
    this.chipsTarget.innerHTML = ""
    const visible = [...this._byId.entries()].filter(([_, v]) => !v.destroyed)
    if (this.hasEmptyTarget) this.emptyTarget.style.display = visible.length ? "none" : ""
    visible.forEach(([id, v]) => {
      const chip = document.createElement("span")
      chip.className = "picker__chip"
      chip.innerHTML = `
        ${v.avatar
          ? `<span class="picker__chip-avatar"><img src="${v.avatar}" alt=""></span>`
          : `<span class="picker__chip-avatar picker__chip-avatar--initials">${this._initials(v.full_name)}</span>`}
        <span class="picker__chip-label">${this._escape(v.full_name)}</span>
        <button type="button" class="picker__chip-remove" data-user-id="${id}" data-action="click->assignees-picker#removeChip" title="Retirer">
          <i class="fa-solid fa-xmark"></i>
        </button>
      `
      this.chipsTarget.appendChild(chip)
    })
  }

  _renderHidden() {
    if (!this.hasHiddenTarget) return
    this.hiddenTarget.innerHTML = ""
    let idx = 0
    for (const [userId, v] of this._byId.entries()) {
      const prefix = `tache[assignments_attributes][${idx++}]`
      if (v.existingId) {
        this._appendHidden(`${prefix}[id]`, v.existingId)
        if (v.destroyed) {
          this._appendHidden(`${prefix}[_destroy]`, "1")
          continue
        }
      } else if (v.destroyed) {
        continue
      }
      this._appendHidden(`${prefix}[user_id]`, userId)
      this._appendHidden(`${prefix}[role]`,    "assignee")
    }
  }

  _appendHidden(name, value) {
    const inp = document.createElement("input")
    inp.type  = "hidden"
    inp.name  = name
    inp.value = value
    this.hiddenTarget.appendChild(inp)
  }

  _closeResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = ""
      this.resultsTarget.classList.remove("picker__results--open")
    }
  }

  _initials(name) {
    return String(name || "?").split(/[\s@\.]+/).filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join("") || "?"
  }

  _escape(s) {
    const div = document.createElement("div")
    div.textContent = String(s ?? "")
    return div.innerHTML
  }
}
